import { useEffect, useState } from 'react'
import api from 'config/axiosConfig'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import FeedbackCard from '../FeedbackCard'
import FeedbackProfileTabs from './FeedbackProfileTabs'
import { Feedback } from 'util/constants'
import { setDocumentTitle } from 'util/pageTitles'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import { useUser } from 'util/UserContext'
import { PrivacyLevel } from 'util/userUtils'
import { Coauthor, CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect'

const FeedbackProfile = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { currentUser } = useUser()
    const [feedback, setFeedback] = useState<Feedback | null>(null)
    const [error, setError] = useState<string>('')
    const [editMode, setEditMode] = useState<boolean>(false)
    const [title, setTitle] = useState<string>("")
    const [description, setDescription] = useState<string>("")
    const [category, setCategory] = useState<Feedback['properties']['category']>("suggestion")
    const [privacy, setPrivacy] = useState<PrivacyLevel>("public")
    const [status, setStatus] = useState<Feedback['status']>("open")
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [searchParams, setSearchParams] = useSearchParams()

    // Validation states
    const [titleValidationMessage, setTitleValidationMessage] = useState<string>("")
    const [descriptionValidationMessage, setDescriptionValidationMessage] = useState<string>("")

    // Add coauthor-related state
    const [coauthors, setCoauthors] = useState<Coauthor[]>(feedback?.properties?.coauthors || [])
    const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([])
    const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([])

    useEffect(() => {
        setDocumentTitle(`Feedback - ${title}`)
    }, [title])

    useEffect(() => {
        const fetchFeedback = async () => {
            if (isNaN(Number(id))) {
                setError('404: Failed to load feedback profile. Please try again later.')
            } else {
                try {
                    const response = await api.get<Feedback>(`/api/feedbacks/id/${id}`)
                    setFeedback(response.data)
                    setTitle(response.data.title)
                    setDescription(response.data.description)
                    setStatus(response.data.status)
                    setCategory(response.data.properties.category)
                    setPrivacy(response.data.properties.privacy)
                    setCoauthors(response.data.properties.coauthors || [])
                    if (searchParams.get('edit') === 'true') {
                        setSearchParams({})
                        
                        if (currentUser?.id === response.data.user_id) {
                            setEditMode(true)
                        }
                    }
                } catch (err) {
                    if (err instanceof AxiosError && err.response?.status === 404) {
                        setError('404: Failed to load feedback profile. Please try again later.')
                    } else {
                        setError('Failed to load feedback profile. Please try again later.')
                    }
                }
            }
        }
        //console.log('FETCHING feedback')
        fetchFeedback()
    }, [id, searchParams, currentUser, navigate, setSearchParams])

    const validateFields = () => {
        let isValid = true
        
        // Title validation
        if (!title.trim()) {
            setTitleValidationMessage('Title is required')
            isValid = false
        } else if (title.length > 300) {
            setTitleValidationMessage('Title must be less than 300 characters')
            isValid = false
        } else {
            setTitleValidationMessage('')
        }

        // Description validation
        if (!description.trim()) {
            setDescriptionValidationMessage('Description is required')
            isValid = false
        } else {
            setDescriptionValidationMessage('')
        }

        return isValid
    }

    // Update handleSave to include coauthors
    const handleSave = async () => {
        if (isSubmitting || !validateFields()) return

        setIsSubmitting(true)
        try {
            // Prepare the updated feedback data
            const response = await api.patch(`/api/feedbacks/${id}`, {
                title,
                description,
                category,
                privacy,
                status
            })
            
            if (response.status === 200) {
      
                // Only make coauthor request if there are changes
                if (newCoauthors.length > 0 || removedCoauthors.length > 0) {
                  try {
                    await api.post(`/api/coauthors/feedback/${id}/request`, {
                      addedCoauthors: newCoauthors.map((c: any) => ({
                        id: c.id,
                        type: c.type
                      })),
                      removedCoauthors: removedCoauthors.map((c: any) => ({
                        id: c.id,
                        type: c.type
                      }))
                    })
                  } catch (err) {
                    console.error('Error updating coauthors:', err)
                  }
                }
                setFeedback(prev => prev ? {
                  ...prev,
                  title,
                  description,
                  status,
                  properties: {
                    ...prev.properties,
                    category,
                    privacy,
                    coauthors: [...coauthors]
                  }
                } : null)
                setIsSubmitting(false)
                setEditMode(false)
                setSearchParams({})
                // Reset coauthor changes after successful save
                setNewCoauthors([])
                setRemovedCoauthors([])
            }
        } catch (error) {
            console.error("Error updating feedback:", error)
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        setEditMode(false)
        setSearchParams({})
    }

    if (error) {
        return error.match(/404/) ? 
            <NotFoundPage 
                displayStyle="page" 
                errorMessage="Sorry, we couldn't find this feedback." 
                errorSubtext="It may be only visible to specific users. Double check the URL or try reloading the page." 
                buttonText="View Other Feedback" 
                buttonHref="/user-feedback" 
            /> : 
            <ServerErrorPage />
    }

    if (!feedback) {
        return <LoadingSkeleton count={1} height="h-80" className="mt-4" />
    }

    const editorButtons = editMode ? (
        <div className="flex justify-end gap-2">
            <button
                onClick={handleCancel}
                className="btn-secondary py-1.5"
                disabled={isSubmitting}
            >
                Cancel
            </button>
            <button
                onClick={handleSave}
                className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
                disabled={isSubmitting || !title || !description || !category}
            >
                Save
            </button>
        </div>
    ) : null;

    return (
        <div>
            <div>
                {feedback && (
                    <FeedbackCard
                        feedback={{
                            ...feedback,
                            title,
                            description,
                            status,
                            properties: {
                                ...feedback.properties,
                                category,
                                privacy,
                            }
                        }}
                        showMeta={true}
                        editMode={editMode}
                        isFeedbackProfilePage={true}
                        onTitleChange={setTitle}
                        onDescriptionChange={setDescription}
                        onCategoryChange={setCategory}
                        onPrivacyChange={setPrivacy}
                        onStatusChange={setStatus}
                        onCoauthorsChange={(added: any, removed: any) => {
                          setCoauthors(prev => {
                            // Remove coauthors that were removed
                            const filtered = prev.filter(
                              (c: any) => !removed.some((r: any) => parseInt(r.id) === parseInt(c.id) && r.type === c.type)
                            );
                            return filtered;
                          });
                            setNewCoauthors(added);
                            setRemovedCoauthors(removed);
                        }}
                        titleValidationMessage={titleValidationMessage}
                        descriptionValidationMessage={descriptionValidationMessage}
                    >
                        {editorButtons}
                    </FeedbackCard>
                )}
            </div>
            <FeedbackProfileTabs feedback={feedback} />
        </div>
    )
}

export default FeedbackProfile
